/* Mission Control: the living Content & Code consoles.
   Participation (both): pointer tilt, sheen, edge light, touch ripples, and figures that tell their context.
   Terminal (Code): 
   Each line is written out once, character by character, with a blinking block cursor, when the card
   comes into view. Figures and product names get syntax-style colour. The full text is in the DOM from
   the start (characters not yet typed are only transparent), so assistive tech, no-JS, reduced motion,
   and the theme editor all get the finished text immediately. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const design = document.documentElement.classList.contains('shopify-design-mode');
  const NAMES = /\b(Jonny|Caipher|App Store|ZeroTo1)\b/g;
  const NUMS = /[<~$]?\d[\d,.]*[+%x]?/g;
  const CPS = 70; // characters per second
  const LINE_PAUSE = 260; // ms between lines

  // Wrap numbers and names in token spans, then every character in its own span
  function tokenize(el) {
    const text = el.textContent;
    const marks = [];
    for (const m of text.matchAll(NUMS)) marks.push({ s: m.index, e: m.index + m[0].length, cls: 'mc-tok-num' });
    for (const m of text.matchAll(NAMES)) if (!marks.some((k) => m.index < k.e && m.index + m[0].length > k.s)) marks.push({ s: m.index, e: m.index + m[0].length, cls: 'mc-tok-name' });
    marks.sort((a, b) => a.s - b.s);
    const frag = document.createDocumentFragment();
    const chars = [];
    let i = 0;
    const put = (str, parent) => {
      for (const ch of str) {
        const c = document.createElement('span');
        c.className = 'mc-ch';
        c.textContent = ch;
        parent.appendChild(c);
        chars.push(c);
      }
    };
    marks.forEach((k) => {
      if (k.s > i) put(text.slice(i, k.s), frag);
      const tok = document.createElement('span');
      tok.className = k.cls;
      put(text.slice(k.s, k.e), tok);
      frag.appendChild(tok);
      i = k.e;
    });
    if (i < text.length) put(text.slice(i), frag);
    el.textContent = '';
    // Assistive tech reads each line whole; the per-character spans are visual only
    const sr = document.createElement('span');
    sr.className = 'mc-visually-hidden';
    sr.textContent = text;
    const vis = document.createElement('span');
    vis.setAttribute('aria-hidden', 'true');
    vis.appendChild(frag);
    el.append(sr, vis);
    return chars;
  }

  function Terminal(card) {
    const term = card.querySelector('.mc-term');
    const lines = [...card.querySelectorAll('[data-mc-type]')];
    if (!term || !lines.length) return;
    const all = lines.map(tokenize);
    // Final state first: always readable if anything below never runs
    const cursor = document.createElement('span');
    cursor.className = 'mc-term__cursor';
    cursor.setAttribute('aria-hidden', 'true');
    lines[lines.length - 1].querySelector('[aria-hidden]').appendChild(cursor);
    if (reduced || design || !('IntersectionObserver' in window)) return;

    term.classList.add('is-typing');
    // Waiting to type: the cursor blinks at the start of the first line
    lines[0].querySelector('[aria-hidden]').prepend(cursor);
    const io = new IntersectionObserver((entries) => {
      if (entries[0].intersectionRatio < 0.3) return; // not merely touching the viewport
      io.disconnect();
      type();
    }, { threshold: 0.35 });
    io.observe(term);

    function type() {
      let li = 0, ci = 0, last = 0, wait = 450, carry = 0;
      const step = (now) => {
        if (!last) last = now;
        let dt = now - last;
        last = now;
        if (wait > 0) { wait -= dt; requestAnimationFrame(step); return; }
        // Characters owed by elapsed time (fractions carry over), so speed is the same at 60Hz and 120Hz
        carry += (dt / 1000) * CPS;
        let budget = Math.floor(carry);
        carry -= budget;
        while (budget-- > 0 && li < all.length) {
          const c = all[li][ci];
          c.classList.add('is-on');
          c.after(cursor);
          ci++;
          if (ci >= all[li].length) { li++; ci = 0; wait = LINE_PAUSE; break; }
        }
        if (li < all.length) requestAnimationFrame(step);
        else { lines[lines.length - 1].querySelector('[aria-hidden]').appendChild(cursor); term.classList.remove('is-typing'); }
      };
      requestAnimationFrame(step);
    }
  }

  /* ----- Participation: the cards answer the visitor's pointer and touch ----- */
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  function Live(card) {
    const sheen = card.querySelector('.mc-console__sheen');
    const status = card.querySelector('[data-mc-status]');
    const statusText = card.querySelector('[data-mc-status-text]');
    const hintHTML = statusText ? statusText.innerHTML : '';
    const figs = [...card.querySelectorAll('[data-mc-fig]')];
    let tx = 0, ty = 0, rx = 0, ry = 0, raf = 0, releaseT = 0, typeRaf = 0;

    const setVars = (x, y) => {
      const r = card.getBoundingClientRect();
      const px = x - r.left, py = y - r.top;
      card.style.setProperty('--sx', `${px.toFixed(0)}px`);
      card.style.setProperty('--sy', `${py.toFixed(0)}px`);
      const ang = (Math.atan2(px - r.width / 2, -(py - r.height / 2)) * 180) / Math.PI;
      card.style.setProperty('--ea', `${ang.toFixed(1)}deg`);
      return { nx: (px / r.width) * 2 - 1, ny: (py / r.height) * 2 - 1, px, py };
    };
    const loop = () => {
      raf = 0;
      rx += (tx - rx) * 0.16; ry += (ty - ry) * 0.16;
      card.style.setProperty('--rx', `${rx.toFixed(2)}deg`);
      card.style.setProperty('--ry', `${ry.toFixed(2)}deg`);
      if (Math.abs(tx - rx) > 0.01 || Math.abs(ty - ry) > 0.01) raf = requestAnimationFrame(loop);
    };
    const tilt = (nx, ny, k = 1) => {
      clearTimeout(releaseT);
      card.classList.remove('is-release');
      card.classList.add('is-tilt', 'is-lit');
      tx = -ny * 3.5 * k; ty = nx * 4.5 * k;
      if (!raf) raf = requestAnimationFrame(loop);
    };
    const release = () => {
      cancelAnimationFrame(raf); raf = 0;
      tx = ty = rx = ry = 0;
      card.classList.add('is-release');
      card.style.setProperty('--rx', '0deg'); card.style.setProperty('--ry', '0deg');
      card.classList.remove('is-lit');
      // Back to no transform at rest, so text renders perfectly crisp
      releaseT = setTimeout(() => {
        card.style.transition = 'none';
        card.classList.remove('is-tilt', 'is-release');
        void card.offsetWidth;
        card.style.transition = '';
      }, 720);
    };
    const ripple = (px, py) => {
      if (!sheen) return;
      const d = document.createElement('span');
      d.className = 'mc-console__ripple';
      d.style.setProperty('--rx0', `${px.toFixed(0)}px`);
      d.style.setProperty('--ry0', `${py.toFixed(0)}px`);
      sheen.appendChild(d);
      d.addEventListener('animationend', () => d.remove(), { once: true });
    };

    if (!reduced && !design) {
      card.addEventListener('pointermove', (e) => {
        if (e.pointerType === 'mouse' && !finePointer.matches) return;
        const v = setVars(e.clientX, e.clientY);
        if (e.pointerType === 'mouse' || e.buttons) tilt(v.nx, v.ny, e.pointerType === 'mouse' ? 1 : 0.6);
      });
      card.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { release(); if (!card.contains(document.activeElement)) showHint(); } });
      card.addEventListener('pointerdown', (e) => {
        const v = setVars(e.clientX, e.clientY);
        ripple(v.px, v.py);
        if (e.pointerType !== 'mouse') { tilt(v.nx, v.ny, 0.7); releaseT = setTimeout(release, 420); }
      });
      card.addEventListener('pointerup', (e) => { if (e.pointerType !== 'mouse') { clearTimeout(releaseT); releaseT = setTimeout(release, 260); } });
      card.addEventListener('pointercancel', () => release());
    }

    // Figures: point, focus, or tap one and it lights, pulses, and tells its story in the readout line
    function write(text) {
      if (!statusText) return;
      cancelAnimationFrame(typeRaf);
      status.classList.add('is-live');
      if (reduced || design) { statusText.textContent = text; return; }
      let i = 0, last = 0;
      const step = (now) => {
        if (!last) last = now;
        i = Math.min(text.length, i + Math.max(1, Math.floor(((now - last) / 1000) * 140)));
        last = now;
        statusText.textContent = text.slice(0, i);
        if (i < text.length) typeRaf = requestAnimationFrame(step);
      };
      typeRaf = requestAnimationFrame(step);
    }
    function showHint() {
      if (!statusText) return;
      cancelAnimationFrame(typeRaf);
      figs.forEach((f) => f.classList.remove('is-hot'));
      status.classList.remove('is-live');
      statusText.innerHTML = hintHTML;
    }
    function hot(el) {
      if (el.classList.contains('is-hot')) return;
      figs.forEach((f) => f.classList.toggle('is-hot', f === el));
      if (!reduced && !design) {
        el.classList.remove('is-pulse'); void el.offsetWidth; el.classList.add('is-pulse');
        setTimeout(() => el.classList.remove('is-pulse'), 720);
      }
      const value = el.querySelector('.mc-readout')?.dataset.value || '';
      const ctx = el.dataset.ctx || el.querySelector('.mc-placard')?.textContent || '';
      write(`${value}: ${ctx}`);
    }
    figs.forEach((f) => {
      f.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hot(f); });
      f.addEventListener('click', () => hot(f));
      f.addEventListener('focus', () => hot(f));
    });
    card.addEventListener('focusout', (e) => { if (!card.contains(e.relatedTarget) && !card.matches(':hover')) showHint(); });
  }

  /* ----- Terminal replay: tap a line and it is typed again, each keystroke flickering ----- */
  function Replay(card) {
    const term = card.querySelector('.mc-term');
    if (!term) return;
    card.querySelectorAll('[data-mc-replay]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (term.classList.contains('is-typing')) return;
        const vis = btn.querySelector('.mc-term__text > [aria-hidden]');
        const chars = [...btn.querySelectorAll('.mc-ch')];
        const cursor = term.querySelector('.mc-term__cursor');
        btn.classList.add('is-replay');
        if (reduced || design || !chars.length) { setTimeout(() => btn.classList.remove('is-replay'), 500); return; }
        chars.forEach((c) => c.classList.remove('is-on', 'is-key'));
        term.classList.add('is-typing');
        if (cursor && vis) vis.prepend(cursor);
        let i = 0, carry = 0, last = 0;
        const step = (now) => {
          if (!last) last = now;
          carry += ((now - last) / 1000) * 110;
          last = now;
          let n = Math.floor(carry); carry -= n;
          while (n-- > 0 && i < chars.length) { const c = chars[i++]; c.classList.add('is-on', 'is-key'); c.after(cursor); }
          if (i < chars.length) requestAnimationFrame(step);
          else {
            term.classList.remove('is-typing');
            btn.classList.remove('is-replay');
            const lastVis = term.querySelector('.mc-term__lines li:last-child .mc-term__text > [aria-hidden]');
            if (cursor && lastVis) lastVis.appendChild(cursor);
            setTimeout(() => chars.forEach((c) => c.classList.remove('is-key')), 260);
          }
        };
        requestAnimationFrame(step);
      });
    });
  }

  function init() {
    document.querySelectorAll('[data-mc-terminal]').forEach((el) => { if (!el.__mcTerm) { el.__mcTerm = 1; Terminal(el); Replay(el); } });
    document.querySelectorAll('[data-mc-console]').forEach((el) => { if (!el.__mcLive) { el.__mcLive = 1; Live(el); } });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
